import React, { useState } from 'react';
import HelpArticleLink from '../HelpArticleLink';
import './BookingManager.css';
import { useBookingResources } from './useBookingResources';
import ResourceManager from './ResourceManager';
import BookingAgenda from './BookingAgenda';
import BookingCalendar from './BookingCalendar';

/**
 * BookingManager — the seller's top-down console for everything booking-related
 * that lives outside per-product settings and order management: calendars &
 * resources, availability, blackouts/openings, and the booking agenda.
 *
 * It's built as a self-contained pane so it can be mounted standalone (on
 * /account/bookings via #r-booking-manager) or embedded later as a tab inside
 * the StorefrontManager tool. Each pane component is semi-independent and could
 * be launched on its own in a modal elsewhere.
 */
const PANES = [
    { key: 'agenda', label: 'Agenda', icon: 'fa-calendar-check' },
    { key: 'calendar', label: 'Calendar', icon: 'fa-calendar-day' },
    { key: 'resources', label: 'Calendars', icon: 'fa-calendar-alt' },
];

export default function BookingManager({ initialPane = 'agenda', defaultTimezone = null }) {
    const resourcesState = useBookingResources();
    const [activePane, setActivePane] = useState(
        PANES.some(p => p.key === initialPane) ? initialPane : PANES[0].key,
    );

    const renderPane = () => {
        switch (activePane) {
            case 'agenda':
                return <BookingAgenda resources={resourcesState.resources} />;
            case 'calendar':
                return <BookingCalendar resources={resourcesState.resources} />;
            case 'resources':
            default:
                return (
                    <ResourceManager
                        resources={resourcesState.resources}
                        loading={resourcesState.loading}
                        error={resourcesState.error}
                        onCreate={resourcesState.create}
                        onUpdate={resourcesState.update}
                        onDelete={resourcesState.remove}
                        onEnsureDefault={resourcesState.ensureDefault}
                        onReload={resourcesState.reload}
                        onResourceChange={resourcesState.upsertLocal}
                        defaultTimezone={defaultTimezone}
                    />
                );
        }
    };

    return (
        <section className="booking-manager" aria-label="Booking & scheduling">
            <header className="booking-manager-header">
                <div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                        <h1>Bookings &amp; scheduling</h1>
                        <HelpArticleLink
                            slug="booking-and-scheduling"
                            label="Help with bookings and scheduling"
                            variant="icon"
                        />
                    </div>
                    <p className="help">
                        Manage your calendars, availability, and reservations in one place.
                    </p>
                    <p className="help">
                        To make a product bookable, enable Scheduling on the product's Details step.
                    </p>
                </div>
            </header>

            <nav className="booking-nav" aria-label="Booking sections">
                {PANES.map(pane => (
                    <button
                        key={pane.key}
                        type="button"
                        className={`booking-nav-item${pane.key === activePane ? ' is-active' : ''}`}
                        onClick={() => setActivePane(pane.key)}
                        aria-current={pane.key === activePane ? 'page' : undefined}
                    >
                        <i className={`fas ${pane.icon}`} aria-hidden="true"></i>
                        <span>{pane.label}</span>
                    </button>
                ))}
            </nav>

            <div className="booking-manager-body">
                {renderPane()}
            </div>
        </section>
    );
}
